import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Fish, 
  Camera, 
  Users, 
  Store, 
  MessageCircle, 
  CreditCard,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Award,
  Calendar,
  Settings,
  Clock,
  Bell,
  Megaphone,
  Tag,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token, logout, refreshUser } = useAuth();
  const [myCatches, setMyCatches] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showMessages, setShowMessages] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [showAnnouncementBanner, setShowAnnouncementBanner] = useState(false);
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);

  useEffect(() => {
    refreshUser();
    fetchMyCatches();
    fetchMessages();
    fetchQRCode();
  }, []);

  const fetchQRCode = async () => {
    try {
      const response = await axios.get(`${API}/member/qr-code`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setQrCodeUrl(url);
    } catch (error) {
      console.error("Error fetching QR code:", error);
    }
  };

  useEffect(() => {
    refreshUser();
    fetchMyCatches();
    fetchMessages();
  }, []);

  const fetchMyCatches = async () => {
    try {
      const response = await axios.get(`${API}/catches/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyCatches(response.data.slice(0, 3));
    } catch (error) {
      console.error("Error fetching catches:", error);
    }
  };

  const fetchMessages = async () => {
    try {
      const [messagesRes, unreadRes] = await Promise.all([
        axios.get(`${API}/messages`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setMessages(messagesRes.data);
      setUnreadCount(unreadRes.data.unread_count);
      // Show announcement banner if there are unread messages
      if (unreadRes.data.unread_count > 0 && messagesRes.data.length > 0) {
        setShowAnnouncementBanner(true);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const handleMarkRead = async () => {
    try {
      await axios.post(`${API}/messages/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking messages read:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navItems = [
    { icon: CreditCard, label: "Dashboard", path: "/dashboard", active: true },
    { icon: Camera, label: "Fishing Log", path: "/fishing-log" },
    { icon: Users, label: "Community", path: "/community" },
    { icon: Store, label: "Retailers", path: "/retailers" },
    { icon: MessageCircle, label: "Q&A", path: "/qa" },
    { icon: Settings, label: "Profile", path: "/profile" },
  ];

  const quickActions = [
    { icon: Camera, label: "Log a Catch", path: "/fishing-log", color: "bg-blue-500" },
    { icon: Users, label: "View Community", path: "/community", color: "bg-green-500" },
    { icon: Store, label: "Get Discounts", path: "/retailers", color: "bg-purple-500" },
    { icon: MessageCircle, label: "Ask a Question", path: "/qa", color: "bg-orange-500" },
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-main)]">
      {/* Announcement Modal */}
      <AnimatePresence>
        {showAnnouncementBanner && messages.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4"
            onClick={() => { setShowAnnouncementBanner(false); handleMarkRead(); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="announcement-modal"
            >
              <div className="bg-gradient-to-r from-[var(--deep-ocean)] to-[var(--deep-ocean)]/80 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Megaphone className="w-8 h-8" />
                    <div>
                      <h2 className="text-xl font-bold">New Announcements</h2>
                      <p className="text-white/70 text-sm">{unreadCount} new message{unreadCount !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => { setShowAnnouncementBanner(false); handleMarkRead(); }}
                    className="p-2 hover:bg-white/20 rounded-full transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto max-h-[60vh] divide-y divide-gray-100">
                {messages.slice(0, 5).map((msg) => (
                  <div 
                    key={msg.id} 
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedMessageId(expandedMessageId === msg.id ? null : msg.id)}
                    data-testid={`announcement-item-${msg.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[var(--bg-main)] flex items-center justify-center flex-shrink-0">
                        {msg.message_type === "announcement" && <Megaphone className="w-5 h-5 text-blue-500" />}
                        {msg.message_type === "promotion" && <Tag className="w-5 h-5 text-green-500" />}
                        {msg.message_type === "alert" && <Bell className="w-5 h-5 text-red-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-[var(--deep-ocean)]">{msg.title}</p>
                          {expandedMessageId === msg.id ? 
                            <ChevronUp className="w-5 h-5 text-[var(--river-stone)] flex-shrink-0" /> : 
                            <ChevronDown className="w-5 h-5 text-[var(--river-stone)] flex-shrink-0" />
                          }
                        </div>
                        <AnimatePresence>
                          {expandedMessageId === msg.id ? (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <p className="text-[var(--river-stone)] mt-2 whitespace-pre-wrap">{msg.content}</p>
                            </motion.div>
                          ) : (
                            <p className="text-sm text-[var(--river-stone)] mt-1 line-clamp-2">{msg.content}</p>
                          )}
                        </AnimatePresence>
                        <p className="text-xs text-[var(--river-stone)] mt-2">
                          {new Date(msg.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-gray-100 bg-gray-50">
                <Button 
                  className="w-full btn-primary"
                  onClick={() => { setShowAnnouncementBanner(false); handleMarkRead(); navigate("/messages"); }}
                  data-testid="view-all-messages-btn"
                >
                  View All Messages
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white card-shadow z-50 transform transition-transform lg:translate-x-0 ${
        menuOpen ? "translate-x-0" : "-translate-x-full"
      }`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <img src="/logo.jpg" alt="FishingFanatix" className="h-10 w-auto" />
            </div>
            <button 
              className="lg:hidden"
              onClick={() => setMenuOpen(false)}
            >
              <X className="w-6 h-6 text-[var(--river-stone)]" />
            </button>
          </div>

          <nav className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  item.active 
                    ? "bg-[var(--deep-ocean)] text-white" 
                    : "text-[var(--river-stone)] hover:bg-[var(--bg-main)]"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}

            {user?.is_admin && (
              <Link
                to="/admin"
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-[var(--sunrise-orange)] hover:bg-[var(--sunrise-orange)]/10 transition-all"
                data-testid="nav-admin"
              >
                <Award className="w-5 h-5" />
                <span className="font-medium">Admin Panel</span>
              </Link>
            )}
          </nav>

          <div className="absolute bottom-6 left-6 right-6">
            <Button
              variant="ghost"
              className="w-full justify-start text-red-500 hover:bg-red-50 hover:text-red-600"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 bg-white/80 backdrop-blur-lg border-b border-gray-100 z-30 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden"
                onClick={() => setMenuOpen(true)}
              >
                <Menu className="w-6 h-6 text-[var(--deep-ocean)]" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[var(--deep-ocean)]">
                  Welcome back, {user?.name}!
                </h1>
                <p className="text-sm text-[var(--river-stone)]">
                  {user?.subscription_status === "active" 
                    ? "Active Member" 
                    : user?.subscription_status === "trial" 
                      ? `Trial - ${user?.days_left} days left`
                      : "Trial Expired"}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Notification Bell */}
              <div className="relative">
                <button
                  onClick={() => { setShowMessages(!showMessages); if (!showMessages) handleMarkRead(); }}
                  className="relative p-2 rounded-full hover:bg-gray-100 transition"
                  data-testid="notifications-btn"
                >
                  <Bell className="w-6 h-6 text-[var(--deep-ocean)]" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Messages Dropdown */}
                {showMessages && (
                  <div className="absolute right-0 top-12 w-96 bg-white rounded-lg card-shadow-deep border border-gray-100 z-50 max-h-[70vh] overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                      <h3 className="font-semibold text-[var(--deep-ocean)]">Notifications</h3>
                      <button 
                        onClick={() => { setShowMessages(false); navigate("/messages"); }}
                        className="text-sm text-[var(--deep-ocean)] hover:underline"
                      >
                        View All
                      </button>
                    </div>
                    {messages.length > 0 ? (
                      <div className="divide-y divide-gray-100 overflow-y-auto flex-1">
                        {messages.slice(0, 5).map((msg) => (
                          <div 
                            key={msg.id} 
                            className="p-4 hover:bg-gray-50 cursor-pointer"
                            onClick={() => setExpandedMessageId(expandedMessageId === msg.id ? null : msg.id)}
                            data-testid={`notification-item-${msg.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-full bg-[var(--bg-main)] flex items-center justify-center flex-shrink-0">
                                {msg.message_type === "announcement" && <Megaphone className="w-4 h-4 text-blue-500" />}
                                {msg.message_type === "promotion" && <Tag className="w-4 h-4 text-green-500" />}
                                {msg.message_type === "alert" && <Bell className="w-4 h-4 text-red-500" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="font-medium text-[var(--deep-ocean)] text-sm">{msg.title}</p>
                                  {expandedMessageId === msg.id ? 
                                    <ChevronUp className="w-4 h-4 text-[var(--river-stone)] flex-shrink-0" /> : 
                                    <ChevronDown className="w-4 h-4 text-[var(--river-stone)] flex-shrink-0" />
                                  }
                                </div>
                                <AnimatePresence>
                                  {expandedMessageId === msg.id ? (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: "auto", opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      className="overflow-hidden"
                                    >
                                      <p className="text-sm text-[var(--river-stone)] mt-2 whitespace-pre-wrap">{msg.content}</p>
                                    </motion.div>
                                  ) : (
                                    <p className="text-xs text-[var(--river-stone)] mt-1 line-clamp-2">{msg.content}</p>
                                  )}
                                </AnimatePresence>
                                <p className="text-xs text-[var(--river-stone)] mt-2">{new Date(msg.created_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                        {messages.length > 5 && (
                          <div className="p-3 text-center">
                            <button 
                              onClick={() => { setShowMessages(false); navigate("/messages"); }}
                              className="text-sm text-[var(--deep-ocean)] hover:underline"
                            >
                              View {messages.length - 5} more messages
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-[var(--river-stone)]">
                        <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No notifications yet</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!user?.has_access && !user?.is_admin && (
                <Button
                  onClick={() => navigate("/subscription")}
                  className="btn-accent"
                  data-testid="activate-membership-btn"
                >
                  Subscribe Now
                </Button>
              )}
              {user?.subscription_status === "trial" && user?.has_access && (
                <Button
                  onClick={() => navigate("/subscription")}
                  variant="outline"
                  className="border-[var(--sunrise-orange)] text-[var(--sunrise-orange)]"
                  data-testid="upgrade-membership-btn"
                >
                  Upgrade to Full Member
                </Button>
              )}
            </div>
          </div>
        </header>

        {/* Trial Banner */}
        {user?.subscription_status === "trial" && user?.has_access && (
          <div className="mx-6 mt-4 p-4 bg-[var(--sunrise-orange)]/10 border border-[var(--sunrise-orange)]/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[var(--sunrise-orange)]" />
                <div>
                  <p className="font-semibold text-[var(--deep-ocean)]">Free Trial - {user?.days_left} days remaining</p>
                  <p className="text-sm text-[var(--river-stone)]">Enjoy full access! Subscribe to continue after your trial.</p>
                </div>
              </div>
              <Button
                onClick={() => navigate("/subscription")}
                className="btn-accent"
                size="sm"
              >
                Subscribe Now
              </Button>
            </div>
          </div>
        )}

        {/* Trial Expired Banner */}
        {!user?.has_access && !user?.is_admin && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-red-500" />
                <div>
                  <p className="font-semibold text-red-700">Your trial has expired</p>
                  <p className="text-sm text-red-600">Subscribe now to regain full access to all features.</p>
                </div>
              </div>
              <Button
                onClick={() => navigate("/subscription")}
                className="bg-red-500 hover:bg-red-600 text-white"
                size="sm"
              >
                Subscribe Now
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Membership Card Section */}
          <section>
            <h2 className="text-lg font-bold text-[var(--deep-ocean)] mb-4">Your Membership Card</h2>
            <div className="perspective-1000">
              <motion.div
                className="relative w-full max-w-md h-56 cursor-pointer preserve-3d"
                animate={{ rotateY: cardFlipped ? 180 : 0 }}
                transition={{ duration: 0.6 }}
                onClick={() => setCardFlipped(!cardFlipped)}
                data-testid="membership-card"
              >
                {/* Front of Card */}
                <div className={`absolute inset-0 backface-hidden membership-card ${user?.is_member_of_month ? "member-card-gold" : ""}`}>
                  <div className="relative z-10 h-full flex flex-col justify-between">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <img src="/logo.jpg" alt="FishingFanatix" className="h-10 w-auto rounded" />
                      </div>
                      {user?.is_member_of_month && (
                        <Badge className="bg-yellow-400 text-yellow-900">
                          <Award className="w-3 h-3 mr-1" /> Member of Month
                        </Badge>
                      )}
                    </div>
                    
                    <div>
                      <p className="text-white/70 text-sm mb-1">Member</p>
                      <p className="text-2xl font-bold">{user?.name} {user?.surname}</p>
                    </div>
                    
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-white/70 text-xs mb-1">Member Number</p>
                        <p className="font-mono text-lg">{user?.member_number}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white/70 text-xs mb-1">Status</p>
                        <Badge className={
                          user?.subscription_status === "active" 
                            ? "bg-[var(--success)]" 
                            : user?.subscription_status === "trial" && user?.has_access
                              ? "bg-[var(--sunrise-orange)]"
                              : "bg-red-500"
                        }>
                          {user?.subscription_status === "active" 
                            ? "ACTIVE" 
                            : user?.subscription_status === "trial" && user?.has_access
                              ? `TRIAL (${user?.days_left}d)`
                              : "EXPIRED"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Back of Card */}
                <div className="absolute inset-0 backface-hidden rotate-y-180 membership-card flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-32 h-32 bg-white rounded-lg mx-auto mb-4 flex items-center justify-center overflow-hidden">
                      {qrCodeUrl ? (
                        <img 
                          src={qrCodeUrl}
                          alt="QR Code"
                          className="w-full h-full object-contain"
                          style={{ imageRendering: 'pixelated' }}
                        />
                      ) : (
                        <div className="text-[var(--deep-ocean)] font-mono text-xs text-center p-2">
                          Loading QR...<br />
                          {user?.member_number}
                        </div>
                      )}
                    </div>
                    <p className="text-white/70 text-sm">Show this to retailers for 10% discount</p>
                  </div>
                </div>
              </motion.div>
            </div>
            <p className="text-sm text-[var(--river-stone)] mt-4">Tap card to flip</p>
          </section>

          {/* Quick Actions */}
          <section>
            <h2 className="text-lg font-bold text-[var(--deep-ocean)] mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action) => (
                <motion.div
                  key={action.path}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card 
                    className="bg-white card-shadow border-0 cursor-pointer hover:card-shadow-deep transition-all"
                    onClick={() => navigate(action.path)}
                    data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <CardContent className="p-6 text-center">
                      <div className={`w-12 h-12 ${action.color} rounded-2xl flex items-center justify-center mx-auto mb-3`}>
                        <action.icon className="w-6 h-6 text-white" />
                      </div>
                      <p className="font-semibold text-[var(--deep-ocean)]">{action.label}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Recent Catches */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[var(--deep-ocean)]">Recent Catches</h2>
              <Link 
                to="/fishing-log" 
                className="text-[var(--deep-ocean)] hover:underline flex items-center gap-1 text-sm font-medium"
                data-testid="view-all-catches-link"
              >
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {myCatches.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-3">
                {myCatches.map((catchItem) => (
                  <Card key={catchItem.id} className="bg-white card-shadow border-0 overflow-hidden">
                    {catchItem.photo_url && (
                      <div className="h-40 bg-gray-100">
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${catchItem.photo_url}`}
                          alt={catchItem.species}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardContent className="p-4">
                      <h3 className="font-bold text-[var(--deep-ocean)]">{catchItem.species}</h3>
                      <p className="text-sm text-[var(--river-stone)]">{catchItem.location}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-[var(--river-stone)]">
                        <Calendar className="w-3 h-3" />
                        {new Date(catchItem.date_caught).toLocaleDateString()}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-white card-shadow border-0">
                <CardContent className="p-8 text-center">
                  <Camera className="w-12 h-12 text-[var(--river-stone)] mx-auto mb-4" />
                  <p className="text-[var(--river-stone)] mb-4">No catches logged yet</p>
                  <Button
                    onClick={() => navigate("/fishing-log")}
                    className="btn-primary"
                    data-testid="log-first-catch-btn"
                  >
                    Log Your First Catch
                  </Button>
                </CardContent>
              </Card>
            )}
          </section>

          {/* Stats */}
          <section>
            <h2 className="text-lg font-bold text-[var(--deep-ocean)] mb-4">Your Stats</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-white card-shadow border-0">
                <CardContent className="p-6 text-center">
                  <p className="text-3xl font-bold text-[var(--deep-ocean)]">{myCatches.length}</p>
                  <p className="text-sm text-[var(--river-stone)]">Total Catches</p>
                </CardContent>
              </Card>
              <Card className="bg-white card-shadow border-0">
                <CardContent className="p-6 text-center">
                  <p className="text-3xl font-bold text-[var(--deep-ocean)]">
                    {user?.fishing_types?.length || 0}
                  </p>
                  <p className="text-sm text-[var(--river-stone)]">Fishing Types</p>
                </CardContent>
              </Card>
              <Card className="bg-white card-shadow border-0">
                <CardContent className="p-6 text-center">
                  <p className="text-3xl font-bold text-[var(--deep-ocean)]">
                    {user?.member_since ? new Date(user.member_since).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' }) : '-'}
                  </p>
                  <p className="text-sm text-[var(--river-stone)]">Member Since</p>
                </CardContent>
              </Card>
              <Card className="bg-white card-shadow border-0">
                <CardContent className="p-6 text-center">
                  <p className="text-3xl font-bold text-[var(--sunrise-orange)]">10%</p>
                  <p className="text-sm text-[var(--river-stone)]">Your Discount</p>
                </CardContent>
              </Card>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
